/**
 * `useAdminRenameTeam` — admin mutation that renames any team (#2877), backed
 * by `PATCH /api/admin/teams/{team_id}/name`. The lead's own rename keeps
 * `useRenameTeam` (`PATCH /api/teams/{id}`): an admin with no seat on a team
 * is a read-only observer there, so that route refuses the write.
 *
 * On success it refreshes the team caches, the Home teams list and every
 * admin console query (teams, detail, activity).
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useAuth } from "../lib/AuthContext";
import { workspacesKey } from "../lib/workspacesKey";
import { type ApiError, apiFetch } from "./client";

export interface AdminRenamedTeam {
  id: string;
  name: string;
}

export function useAdminRenameTeam(teamId: string | undefined) {
  const { accountId } = useAuth();
  const queryClient = useQueryClient();

  return useMutation<AdminRenamedTeam, ApiError, string>({
    mutationFn: (name: string) =>
      apiFetch<AdminRenamedTeam>(`/api/admin/teams/${teamId}/name`, {
        method: "PATCH",
        body: JSON.stringify({ name }),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["team", teamId ?? ""] });
      void queryClient.invalidateQueries({ queryKey: ["teams"] });
      void queryClient.invalidateQueries({ queryKey: ["admin"] });
      void queryClient.invalidateQueries({ queryKey: workspacesKey(accountId) });
    },
  });
}
